/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonLayout } from '../UNSAFE_ButtonLayout';
import { ChevronDownIcon } from '../PRIVATE_ThemedIcons/ChevronDownIcon';
import { Menu } from '../UNSAFE_Menu';
import { useMenuAction, MenuToggleDetail } from '../hooks/UNSAFE_useMenuAction';
import { BaseButton } from '../UNSAFE_BaseButton';

import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeProps } from '../utils/UNSAFE_mergeProps';

import { ComponentChildren, ComponentChild, Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLElement>,
  | 'autofocus'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type Props = IntrinsicProps &
  WidthInterpolationProps & {
    /**
     * children - One or more MenuItems, dividers, or Menu groups
     */
    children?: ComponentChildren;

    /**
     * The icon shown to the left of the label (or to the right of the
     * label in right to left mode). Note that this is not the menu
     * expand icon shown to the right of the label (or to the left of
     * the label in right to left mode).
     */
    icon?: ComponentChild;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * The button label
     */
    label?: string;

    /**
     * Specifies if the menu is open
     */
    isMenuOpen?: boolean;

    /**
     * Property that triggers a callback immediately when toggle happens and value of expanded property should be updated
     */
    onToggleMenu?: (details: MenuToggleDetail) => void;

    /**
     * A suffix to the label that can be rendered differently.  Used to identify
     * which child MenuItem is invoked.
     */
    suffix?: string;

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI."
     */
    variant?: 'borderless' | 'outlined' | 'solid';

    /**
     * The size indicates how large the button is rendered: "sm", "md" (default), "lg".
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the button.
     */
    'aria-describedby'?: string;
  };

/**
 * A MenuButton is a combined action and menu button.
 */
export const MenuButton = forwardRef(
  (
    {
      children,
      label,
      suffix,
      variant = 'outlined',
      isDisabled = false,
      isMenuOpen = false,
      onToggleMenu,
      size = 'md',
      width,
      icon,
      'aria-describedby': ariaDescribedBy,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    }: Props,
    ref?: ForwardedRef<HTMLButtonElement>
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref!, () => buttonRef.current!, [buttonRef]);

    const { triggerProps, menuProps } = useMenuAction({
      isDisabled: isDisabled,
      onToggleMenu: onToggleMenu,
      isMenuOpen: isMenuOpen,
      anchorRef: buttonRef
    });

    const globalEventProps = {
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    };

    const rootButton = (
      <BaseButton
        {...mergeProps(triggerProps, globalEventProps)}
        styling={isMenuOpen ? ['min', 'active'] : ['min']}
        isDisabled={isDisabled}
        variant={variant}
        width={width}
        size={size}
        ref={buttonRef}
        aria-label={`${label} ${suffix}`}
        aria-describedby={ariaDescribedBy}>
        <ButtonLayout
          display={'all'}
          suffix={suffix}
          startIcon={icon}
          endIcon={<ChevronDownIcon />}
          size={size}>
          {label}
        </ButtonLayout>
      </BaseButton>
    );

    return isDisabled ? (
      rootButton
    ) : (
      <Fragment>
        {rootButton}
        <Menu {...menuProps}>{children}</Menu>
      </Fragment>
    );
  }
);
